import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import { Text, Box } from 'components/ui';
import {
    space, radii, colors, fonts, fontWeights, gradients
} from 'style/config';
import store from 'store/Store';
import SuperChatActions from 'ai/actions/superChatActions';
import Flag from 'components/Flag/Flag';
import { ENTRYPOINTS } from 'ai/constants/superchat';
import { getCategoryLabelKey, shouldShowDefaultChatLabel, getNewEntryPoint } from 'ai/utils/aiBeautyChat';

function AIBeautyChatContent(props) {
    const {
        localization, page, featuresAIBeautyChat, contextPrompts, handlePromptClick
    } = props;

    const handleStartChat = () => {
        const isSuperChatOpen = store.getState()?.superChat?.showSuperChat;

        if (!isSuperChatOpen) {
            const contextEntryPoint = getNewEntryPoint('', page);
            const entryPointData = contextEntryPoint || { entrypoint: ENTRYPOINTS.DEFAULT };
            entryPointData.source = 'header';

            store.dispatch(SuperChatActions.showSuperChat(true, contextPrompts || [], entryPointData));
        }
    };

    const onPromptClick = promptContent => {
        if (handlePromptClick && page && featuresAIBeautyChat) {
            handlePromptClick(contextPrompts, page, featuresAIBeautyChat, promptContent);
        }
    };

    // Use contextual prompts from Redux store
    const suggestedPrompts = contextPrompts || [];

    // Determine the label to display based on page context
    // Pages like Homepage, Basket, Love List, SRP, and PDP should always show "Try Asking"
    const showDefaultLabel = shouldShowDefaultChatLabel(page);
    const categoryLabelKey = !showDefaultLabel ? getCategoryLabelKey(page) : null;
    const displayLabel = showDefaultLabel || !categoryLabelKey ? localization?.tryAsking : localization?.[categoryLabelKey];

    return (
        <Box
            display='flex'
            flexDirection='column'
            minWidth='374px'
        >
            {/* Top Section */}
            <Box
                display='flex'
                flexDirection='column'
                gap={`${space[3]}px`}
                padding={`0 ${space[4]}px`}
            >
                {/* Description Box */}
                <Box
                    backgroundColor='rgba(255, 255, 255, 0.2)'
                    css={styles.descriptionBox}
                >
                    <Text
                        fontSize='base'
                        lineHeight='base'
                        fontFamily={fonts.base}
                    >
                        {localization?.description}
                    </Text>
                </Box>

                {/* Start Chat CTA */}
                <button
                    type='button'
                    onClick={handleStartChat}
                    css={styles.startChatButton}
                >
                    <Text
                        fontSize='base'
                        fontWeight='bold'
                        lineHeight='base'
                        textAlign='center'
                        color={colors.black}
                        fontFamily={fonts.base}
                    >
                        {localization?.startChat}
                    </Text>
                </button>
            </Box>

            {/* Gray Spacer */}
            <Box
                height={space[2]}
                backgroundColor='nearWhite'
                width='100%'
                marginTop={space[4]}
            />

            {/* Bottom Section - Try Asking */}
            <Box
                display='flex'
                flexDirection='column'
                gap={`${space[3]}px`}
                padding={`${space[4]}px`}
            >
                <Text
                    fontSize='md'
                    fontWeight='bold'
                    lineHeight='tight'
                >
                    {displayLabel}
                </Text>
                {suggestedPrompts.length > 0
                    ? suggestedPrompts.map((prompt, index) => {
                        const promptText = prompt?.content || prompt;
                        const hasNewFlag = prompt?.is_new || false;

                        return (
                            <button
                                key={index}
                                type='button'
                                onClick={() => onPromptClick(promptText)}
                                css={styles.promptItem}
                            >
                                <Text
                                    fontSize='base'
                                    lineHeight='base'
                                    fontFamily={fonts.base}
                                    css={styles.promptText}
                                >
                                    {promptText}
                                </Text>
                                {hasNewFlag && (
                                    <Flag
                                        backgroundColor='black'
                                        fontSize={10}
                                        paddingX={space[1]}
                                        paddingY={0}
                                        css={styles.newFlag}
                                    >
                                        {localization?.new}
                                    </Flag>
                                )}
                            </button>
                        );
                    })
                    : null}
            </Box>
        </Box>
    );
}

const styles = {
    descriptionBox: {
        flex: '0 0 auto',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: radii[5],
        width: '100%'
    },
    startChatButton: {
        display: 'flex',
        height: '44px',
        padding: space[3],
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: space[3],
        alignSelf: 'stretch',
        border: 'none',
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
        width: '100%',
        borderRadius: radii.full,
        backgroundColor: 'transparent',
        backgroundImage: gradients.aiBeautyChat,
        textDecoration: 'none',
        margin: 0,
        '&:hover': {
            opacity: 0.9
        },
        '&:active': {
            opacity: 0.8
        }
    },
    newFlag: {
        position: 'relative',
        bottom: '1px',
        whiteSpace: 'pre-wrap',
        borderRadius: radii[2],
        textTransform: 'uppercase',
        fontSize: '10px',
        display: 'inline-flex',
        fontWeight: fontWeights.bold,
        backgroundColor: colors.black,
        color: colors.white,
        height: '13px',
        width: '36px',
        marginLeft: space[1],
        textAlign: 'center',
        justifyContent: 'center',
        alignItems: 'center'
    },
    promptItem: {
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
        width: '100%',
        '&:hover': {
            opacity: 0.7
        }
    },
    promptText: {
        flex: 1
    }
};

export default wrapFunctionalComponent(AIBeautyChatContent, 'AIBeautyChatContent');
